import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { UserCellComponent } from '../../../shared/components/ui/user-cell/user-cell.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { minioUrl } from '../../../shared/helpers/minio';
import { DepositService } from '../../../shared/services/deposit.service';
import { InvoicesService } from '../../../shared/services/invoices.service';
import {
  INVOICE_STATUS_META,
  Invoice,
} from '../../../shared/types/invoice.types';
import {
  METADATA_TYPE_LABEL,
  PaymentTransactionView,
  TRANSACTION_STATUS_META,
  parseTransactionMetadata,
} from '../../../shared/types/transaction.types';
import { TPipe } from '../../../shared/pipe/t.pipe';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { serverDate } from '../../../shared/helpers/dates';

/** Ligne affichée : la vue relayée + le label du type métier pré-calculé. */
type LinkedTxRow = PaymentTransactionView & { _metadataTypeLabel: string };

/**
 * Détail self-service d'une facture du user courant. Endpoint `/api/invoices/me/{id}`.
 * Vue consultative + récap des transactions de paiement associées.
 *
 * Pas d'action admin (mark-paid / cancel) : côté owner on ne fait que consulter.
 * Le paiement effectif passe par le flow customer (mobile) ou le bouton "Payer"
 * sur les factures en attente (à ajouter ultérieurement si besoin).
 */
@Component({
  selector: 'app-invoice-detail',
  imports: [CommonModule, RouterModule, DatePipe, DecimalPipe, PageHeaderComponent, DataStateComponent, UserCellComponent, TPipe, LocalizedDatePipe],
  templateUrl: './invoice-detail.component.html',
})
export class InvoiceDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly invoicesService = inject(InvoicesService);
  private readonly depositService = inject(DepositService);
  private readonly destroyRef = inject(DestroyRef);

  readonly invoice = signal<Invoice | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  // ── Déclaration remboursement caution cash (#46) ────────────────────────
  readonly declaring = signal(false);
  readonly declareError = signal<string | null>(null);
  readonly declareDone = signal(false);

  // ── PDF (#98) — toujours via `/download` ──────────────────────────────
  // Le back y régénère le PDF s'il manque (génération best-effort à la création)
  // ou s'il est périmé (statut payée / annulée / frais de retard — 2026-10-23),
  // puis renvoie la clé MinIO à jour. `pdfUrl` reste un instantané : on ne lie
  // jamais dessus directement.
  readonly pdfBusy = signal(false);
  readonly pdfError = signal<string | null>(null);

  /** Linked payment transactions (lazy-loaded after invoice fetch). */
  readonly linkedTxs = signal<LinkedTxRow[]>([]);
  readonly linkedTxsLoading = signal(false);
  readonly txStatusMeta = TRANSACTION_STATUS_META;
  readonly statusMeta = INVOICE_STATUS_META;

  /** True when the invoice is past due and unpaid. */
  readonly isOverdue = computed(() => {
    const inv = this.invoice();
    if (!inv?.dueDate) return false;
    if (inv.status === 'PAID' || inv.status === 'CANCELLED' || inv.status === 'REFUNDED') return false;
    return serverDate(inv.dueDate).getTime() < Date.now();
  });

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.invoicesService
      .getMine(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (inv) => {
          this.invoice.set(inv);
          this.loading.set(false);
          this.fetchLinkedTxs();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.declareDone.set(false);
          this.loadError.set(
            toErrorMessage(err, 'errors.invoiceLoadFailed'),
          );
        },
      });
  }

  /**
   * PDF absent (`pdfUrl` null) → `GET /{id}/download` régénère côté back
   * (~1-2 s) et renvoie l'URL. On patche la facture locale — le bouton
   * redevient un lien direct — et on tente l'ouverture ; si le navigateur
   * bloque le popup (appel asynchrone), le lien patché reste cliquable.
   */
  downloadPdf(): void {
    const inv = this.invoice();
    if (!inv || this.pdfBusy()) return;
    this.pdfBusy.set(true);
    this.pdfError.set(null);
    this.invoicesService
      .download(inv.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (key) => {
          // `/download` renvoie la **clé d'objet** (texte brut), pas une URL.
          this.pdfBusy.set(false);
          this.invoice.set({ ...inv, pdfUrl: key });
          const full = minioUrl(key);
          if (full) window.open(full, '_blank', 'noopener');
        },
        error: (err: unknown) => {
          this.pdfBusy.set(false);
          this.pdfError.set(
            toErrorMessage(err, 'errors.pdfGenerateFailed', {
              404: 'errors.pdfRetry',
            }),
          );
        },
      });
  }

  /**
   * Best-effort : transactions de paiement de la facture, **relayées par
   * rental** (`GET /invoices/{id}/transactions`, §2.8.bis). L'ancien appel
   * direct `payment /transactions/by-source` est réservé ROLE_ADMIN → il
   * répondait 403 pour un owner et ce bloc restait vide. Ne pas y revenir.
   */
  private fetchLinkedTxs(): void {
    this.linkedTxsLoading.set(true);
    this.invoicesService
      .transactions(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (txs) => {
          // VM : label du type métier (metadata.type) + tri plus récent d'abord.
          const rows: LinkedTxRow[] = [...(txs ?? [])]
            .map((t) => {
              const meta = parseTransactionMetadata(t.metadata);
              return {
                ...t,
                _metadataTypeLabel: meta?.type
                  ? METADATA_TYPE_LABEL[meta.type] ?? meta.type
                  : '',
              };
            })
            .sort((a, b) => {
              const ta = a.createdAt ? serverDate(a.createdAt).getTime() : 0;
              const tb = b.createdAt ? serverDate(b.createdAt).getTime() : 0;
              return tb - ta;
            });
          this.linkedTxs.set(rows);
          this.linkedTxsLoading.set(false);
        },
        error: () => {
          this.linkedTxs.set([]);
          this.linkedTxsLoading.set(false);
        },
      });
  }

  /**
   * (Loueur) Déclare avoir remboursé la caution en espèces (#46). Nécessite
   * `invoice.refundRequestId` (dépendance back : à exposer sur l'IRead). Sur
   * succès, recharge la facture (statut caution à jour).
   */
  declareCashRefund(): void {
    const inv = this.invoice();
    const refundId = inv?.refundRequestId;
    if (!refundId || this.declaring()) return;
    this.declaring.set(true);
    this.declareError.set(null);
    this.depositService
      .declareCashSettled(refundId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.declaring.set(false);
          this.declareDone.set(true);
          this.fetch();
        },
        error: (err: unknown) => {
          this.declaring.set(false);
          this.declareError.set(toErrorMessage(err, 'errors.declareFailed'));
        },
      });
  }
}
